// ─── RdvQuestionsBlock ───────────────────────────────────────────────────
// 2026-07-07 — Bloc « Questions du RDV ». Transforme le briefing IA (lecture
// seule) en outil actionnable pour l'anamnèse :
//   - les questions suggérées par l'IA (briefing.questions_to_explore) deviennent
//     COCHABLES (Anissa garde celles qu'elle veut) ;
//   - Anissa peut AJOUTER ses propres questions ;
//   - chaque question retenue a un champ RÉPONSE qu'elle remplit au fil du RDV.
//
// Les réponses vivent dans client.form.rdv_questions (donc dans l'anamnèse) et
// nourrissent la génération du plan via buildRdvNotesBlockFr/En
// (src/services/prompts/nutrition/_clinicalContext.fr.js + en.js). Garde-fou :
// seules les entrées cochées ET renseignées sont injectées ; les pistes
// d'analyses (suggested_analyses) ne passent JAMAIS dans le plan.
//
// Composant AUTONOME, CONTRÔLÉ et réutilisable : il ne persiste jamais lui-même.
// Il reçoit la liste `questions` (form.rdv_questions) + une callback `onChange`
// qui remonte la NOUVELLE liste au parent. Chaque parent branche sa persistance :
//   - AnissaClientForm (anamnèse, state local) : onChange = updateField('rdv_questions', …)
//     → persisté au submit du formulaire, comme les autres champs.
//   - StepAnamnesis (parcours) : onChange = saveClient + refresh (persistance immédiate).
// Le seed initial (depuis le briefing IA) est calculé par le helper exporté
// seedRdvQuestions, appelé par le parent pour rester une fonction pure.
//
// Cockpit praticienne : sobre/fonctionnel, pas d'emoji superflu. Anissa reste
// maîtresse : elle coche, écrit, ajoute — aucune décision auto.

import { useState } from 'react';

/**
 * Seed initial des questions du RDV depuis le briefing IA du form.
 * Une entrée par question IA (source:'ia', décochée, réponse vide).
 * À n'appeler QUE si form.rdv_questions est absent (jamais d'écrasement).
 * Fonction pure, fail-safe.
 * @param {object|null|undefined} form
 * @returns {Array<{id,text,source,included,answer}>}
 */
export function seedRdvQuestions(form) {
  const iaQuestions = form?.ia_briefing?.output?.questions_to_explore;
  if (!Array.isArray(iaQuestions)) return [];
  return iaQuestions
    .map((text) => String(text ?? '').trim())
    .filter(Boolean)
    .map((text) => ({
      id: `ia-${crypto.randomUUID()}`,
      text,
      source: 'ia',
      included: false,
      answer: '',
    }));
}

/**
 * Résout la liste à afficher : rdv_questions existant, sinon seed depuis le
 * briefing IA. Ne persiste rien (le seed n'est écrit qu'au premier geste réel).
 */
export function resolveRdvQuestions(form) {
  if (Array.isArray(form?.rdv_questions)) return form.rdv_questions;
  return seedRdvQuestions(form);
}

/**
 * @param {object} props
 * @param {Array} props.questions  Liste courante (form.rdv_questions résolu).
 * @param {(next:Array)=>void} props.onChange  Remonte la nouvelle liste au parent.
 * @param {'journey'|'form'} [props.variant]  Habillage : 'journey' (tokens --jrn-*,
 *   surface autonome) ou 'form' (section intégrée au formulaire d'anamnèse).
 */
export default function RdvQuestionsBlock({ questions = [], onChange, variant = 'journey' }) {
  const [newQuestion, setNewQuestion] = useState('');

  const emit = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  const toggleIncluded = (id) => {
    emit(questions.map((q) => (q.id === id ? { ...q, included: !q.included } : q)));
  };
  const updateAnswer = (id, answer) => {
    emit(questions.map((q) => (q.id === id ? { ...q, answer } : q)));
  };
  const removeQuestion = (id) => {
    emit(questions.filter((q) => q.id !== id));
  };
  const addQuestion = () => {
    const text = newQuestion.trim();
    if (!text) return;
    emit([
      ...questions,
      { id: `anissa-${crypto.randomUUID()}`, text, source: 'anissa', included: true, answer: '' },
    ]);
    setNewQuestion('');
  };

  const isJourney = variant === 'journey';

  // Le parcours (journey) est sur fond CLAIR (tokens --jrn-*). Le formulaire
  // d'anamnèse (form) est sur fond SOMBRE : on bascule vers des couleurs claires
  // pour rester lisible (sinon texte foncé sur fond foncé, illisible).
  const textColor = isJourney ? 'var(--jrn-text, #2a2a26)' : '#f0f0e8';
  const mutedColor = isJourney ? 'var(--jrn-text-muted, #8a8a82)' : 'rgba(240, 240, 232, 0.55)';
  const softColor = isJourney ? 'var(--jrn-text-soft, #6a6a62)' : 'rgba(240, 240, 232, 0.72)';
  const borderColor = isJourney ? 'var(--jrn-border, #d8d8d2)' : 'rgba(255, 255, 255, 0.14)';

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${isJourney ? 'var(--jrn-border, #d8d8d2)' : 'rgba(255, 255, 255, 0.18)'}`,
    borderRadius: 6,
    background: isJourney ? 'var(--jrn-surface-2, transparent)' : 'rgba(255, 255, 255, 0.04)',
    color: isJourney ? 'inherit' : '#f0f0e8',
    fontSize: 'var(--jrn-text-sm, 14px)',
    fontFamily: 'inherit',
  };

  // Parcours (journey) = SÉLECTION : toutes les questions à cocher, SANS champ
  // réponse. Anamnèse (form) = RÉPONSES : uniquement les questions retenues,
  // chacune avec son champ réponse (pas de case à cocher — la sélection s'est
  // faite dans le parcours).
  const visible = isJourney ? questions : questions.filter((q) => q.included === true);
  const emptyText = isJourney
    ? "Aucune question pour l’instant. Préparez le briefing pour obtenir des suggestions, ou ajoutez les vôtres ci-dessous."
    : "Aucune question retenue. Cochez d'abord les questions à poser dans l'étape Onboarding du parcours, ou ajoutez-en une ici.";

  const list = (
    <>
      {visible.length === 0 ? (
        <p style={{ fontSize: 'var(--jrn-text-sm, 14px)', color: mutedColor, margin: 0 }}>
          {emptyText}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {visible.map((q) => (
            <li key={q.id} style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {isJourney && (
                  <input
                    type="checkbox"
                    checked={q.included === true}
                    onChange={() => toggleIncluded(q.id)}
                    style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }}
                    aria-label="Retenir cette question"
                  />
                )}
                <span style={{ flex: 1, fontSize: 'var(--jrn-text-sm, 14px)', color: textColor, lineHeight: 1.5 }}>
                  {q.text}
                  {q.source === 'anissa' && (
                    <span style={{ fontSize: 'var(--jrn-text-xs, 12px)', color: mutedColor, marginLeft: 6 }}>
                      (ajoutée)
                    </span>
                  )}
                </span>
                {q.source === 'anissa' && (
                  <button
                    type="button"
                    onClick={() => removeQuestion(q.id)}
                    style={{ padding: '2px 8px', fontSize: 16, lineHeight: 1, flexShrink: 0, cursor: 'pointer', background: 'transparent', border: `1px solid ${borderColor}`, borderRadius: 6, color: softColor }}
                    aria-label="Supprimer cette question"
                    title="Supprimer"
                  >
                    ×
                  </button>
                )}
              </div>
              {!isJourney && (
                <textarea
                  value={q.answer || ''}
                  onChange={(e) => updateAnswer(q.id, e.target.value)}
                  placeholder="Réponse / notes"
                  rows={2}
                  style={{ ...inputStyle, marginTop: 10, width: '100%', resize: 'vertical' }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <input
          type="text"
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuestion(); } }}
          placeholder="Ajouter une question…"
          style={inputStyle}
        />
        <button
          type="button"
          onClick={addQuestion}
          disabled={!newQuestion.trim()}
          style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 6, border: `1px solid ${borderColor}`, background: 'var(--jrn-surface-2, transparent)', color: textColor, cursor: newQuestion.trim() ? 'pointer' : 'not-allowed', opacity: newQuestion.trim() ? 1 : 0.5 }}
        >
          Ajouter
        </button>
      </div>

      <p style={{ fontSize: 'var(--jrn-text-xs, 12px)', color: mutedColor, marginTop: 12, marginBottom: 0, lineHeight: 1.5 }}>
        {isJourney
          ? "Cochez les questions à poser au RDV. Vous noterez les réponses dans l'anamnèse, avec la cliente."
          : "Ces réponses sont jointes à l'anamnèse pour la génération du plan."}
      </p>
    </>
  );

  // Variant 'journey' : bloc autonome titré (cockpit parcours).
  if (isJourney) {
    return (
      <div style={{ marginBottom: 'var(--jrn-6, 32px)' }}>
        <p className="jrn-label">Questions du RDV</p>
        <p style={{ fontSize: 'var(--jrn-text-sm, 14px)', color: softColor, marginTop: 4, marginBottom: 12, lineHeight: 1.55 }}>
          Cochez les questions à poser au RDV et ajoutez les vôtres. Vous noterez les réponses dans l'anamnèse.
        </p>
        <div className="jrn-surface" style={{ padding: 'var(--jrn-6, 24px)' }}>
          {list}
        </div>
      </div>
    );
  }

  // Variant 'form' : section intégrée au formulaire d'anamnèse (h4 + wrapper léger).
  return (
    <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 15 }}>Questions du RDV</h4>
      <p style={{ fontSize: 13, color: softColor, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
        Notez les réponses aux questions retenues, au fil de l'anamnèse avec la cliente.
      </p>
      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 8, padding: 16 }}>
        {list}
      </div>
    </div>
  );
}
